import { useEffect, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { OpportunityConfirmModal } from "@/components/opportunity/OpportunityConfirmModal";
import {
  OpportunityOverviewCard,
  OpportunityReadOnlyRow,
} from "@/components/opportunity/OpportunityOverviewPrimitives";
import { OrgFormStartAction } from "@/components/opportunity/OrgFormStartAction";
import { updateOpportunityCustomer } from "@/lib/opportunity/opportunityClient";

type CustomerDraft = {
  project_name: string;
  customer_name: string;
  customer_company: string;
  customer_phone: string;
  customer_email: string;
  customer_address: string;
};

function draftFromOpportunity(opportunity: Opportunity): CustomerDraft {
  return {
    project_name: opportunity.project_name ?? "",
    customer_name: opportunity.customer_name ?? "",
    customer_company: opportunity.customer_company ?? "",
    customer_phone: opportunity.customer_phone ?? "",
    customer_email: opportunity.customer_email ?? "",
    customer_address: opportunity.customer_address ?? "",
  };
}

export function OpportunityCustomerCard(props: {
  opportunity: Opportunity;
  onChanged: () => void;
  setMessage: (msg: { text: string; ok: boolean } | null) => void;
}) {
  const { opportunity, onChanged, setMessage } = props;
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomerDraft>(() => draftFromOpportunity(opportunity));

  useEffect(() => {
    setDraft(draftFromOpportunity(opportunity));
    setEditing(false);
    setConfirmOpen(false);
    setError(null);
  }, [opportunity.id, opportunity.updated_at]);

  function cancelEdit() {
    setDraft(draftFromOpportunity(opportunity));
    setEditing(false);
    setConfirmOpen(false);
    setError(null);
  }

  async function save() {
    if (!draft.project_name.trim() && !draft.customer_name.trim() && !draft.customer_company.trim()) {
      setError("Add at least a project name, customer name, or company name.");
      setConfirmOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    const r = await updateOpportunityCustomer({
      opportunity_id: opportunity.id,
      project_name: draft.project_name,
      customer_name: draft.customer_name,
      customer_company: draft.customer_company,
      customer_phone: draft.customer_phone,
      customer_email: draft.customer_email,
      customer_address: draft.customer_address,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      setConfirmOpen(false);
      return;
    }
    setConfirmOpen(false);
    setEditing(false);
    setMessage({ text: "Customer information updated.", ok: true });
    onChanged();
  }

  return (
    <>
      <OpportunityOverviewCard
        title="Customer"
        headerRight={
          <div className="flex items-center gap-2">
            <OrgFormStartAction compact sourceType="opportunity" opportunityId={opportunity.id} />
            {editing ? (
              <>
                <button
                  type="button"
                  className="text-xs text-gray-600 hover:text-gray-900 dark:text-zinc-300 dark:hover:text-zinc-100"
                  onClick={cancelEdit}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300"
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy}
                >
                  Save
                </button>
              </>
            ) : (
              <button
                type="button"
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            )}
          </div>
        }
      >
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
            {error}
          </div>
        ) : null}
        {editing ? (
          <div className="space-y-2">
            <EditField
              label="Project"
              value={draft.project_name}
              onChange={(v) => setDraft((d) => ({ ...d, project_name: v }))}
            />
            <EditField
              label="Name"
              value={draft.customer_name}
              onChange={(v) => setDraft((d) => ({ ...d, customer_name: v }))}
            />
            <EditField
              label="Company"
              value={draft.customer_company}
              onChange={(v) => setDraft((d) => ({ ...d, customer_company: v }))}
            />
            <EditField
              label="Phone"
              value={draft.customer_phone}
              onChange={(v) => setDraft((d) => ({ ...d, customer_phone: v }))}
            />
            <EditField
              label="Email"
              value={draft.customer_email}
              onChange={(v) => setDraft((d) => ({ ...d, customer_email: v }))}
            />
            <label className="block space-y-1">
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                Address
              </span>
              <AddressAutocompleteInput
                value={draft.customer_address}
                onChange={(v) => setDraft((d) => ({ ...d, customer_address: v }))}
                disabled={busy}
                placeholder="123 Main St"
              />
            </label>
          </div>
        ) : (
          <>
            <OpportunityReadOnlyRow label="Project" value={opportunity.project_name} />
            <OpportunityReadOnlyRow label="Name" value={opportunity.customer_name} />
            <OpportunityReadOnlyRow label="Company" value={opportunity.customer_company} />
            <OpportunityReadOnlyRow label="Phone" value={opportunity.customer_phone} />
            <OpportunityReadOnlyRow label="Email" value={opportunity.customer_email} />
            <OpportunityReadOnlyRow label="Address" value={opportunity.customer_address} />
          </>
        )}
      </OpportunityOverviewCard>
      <OpportunityConfirmModal
        open={confirmOpen}
        title="Update customer information?"
        confirmLabel="Yes, update"
        busy={busy}
        onConfirm={() => void save()}
        onCancel={() => setConfirmOpen(false)}
      >
        <p>Are you sure you want to do that?</p>
        <p>This replaces the customer snapshot on this opportunity. Proposals you generate after this will use the new details.</p>
      </OpportunityConfirmModal>
    </>
  );
}

function EditField(props: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {props.label}
      </span>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
      />
    </label>
  );
}
